import { easeInOutCubic, easeOutCubic, lerp, pneu, seg, smoothstep } from './easing'
import { cameraAt, cameraVars, register } from './timelines'

/**
 * The screen-print film as a pure function of its progress p (0..1).
 * One pass on a flatbed head that rides the X axis. With the screen up, the
 * flood bar (left of the head) drags the ink pool left to right and leaves a
 * flood coat that fills the stencil's openings; the screen lowers to its
 * off-contact gap; the squeegee (right of the head) presses the mesh onto
 * the panel and strokes right to left with its top leading — the ink bead
 * rolls ahead of the edge, the mesh V trails it, and behind it the openings
 * keep only a residue; the screen lifts level and the printed scales stand
 * white on the black panel. The head starts and ends on the left stop.
 *
 * Mesh-plane values (edge, bead, coat, fill) are in face units u = world x
 * - 660, the left scale's centre; screenParts.jsx draws the mesh in that face.
 *
 * It loops on a match cut: the film ends tight on the printed scale and the
 * next loop opens tight on the same scale open in the raised stencil, then
 * pulls back to a blank panel. The machine itself ends where it starts.
 */

const SCREEN_GEOMETRY = { home: 516, far: 1124, up: -150, u0: 660 }
const { home: HOME, far: FAR, up: UP, u0: U0 } = SCREEN_GEOMETRY
const SQ = { up: -30, touch: -12, top: 31, height: 44, lean: 0.176, flexK: 0.0184 }
const FL = { up: -30, edge: -28 }
/* The pool rides ahead of the blade that drives it, clear of the holders
   that would hide it from this camera: 26 ahead of the flood bar, 38 ahead
   of the squeegee edge (where the squeegee takes it over, seamlessly). */
const BEAD = { flood: 26, print: 38 }
/* The coat's left end lies under the frame's left bar. */
const COAT_FROM = 468

export const SCREEN_STATIONS = [0, 0.28, 0.64]
export const SCREEN_BEATS = [0.07, 0.295, 0.72]

const FLOOD = [0.07, 0.2]
const PRINT = [0.295, 0.62]

/** Constant-speed stroke with 8 % eased ends (trapezoidal velocity). */
function stroke(k) {
  const e = 0.08
  const v = 1 / (1 - e)
  if (k < e) return (v * k * k) / (2 * e)
  if (k <= 1 - e) return v * (k - e / 2)
  return 1 - (v * (1 - k) ** 2) / (2 * e)
}

/** Carriage x: flood stroke out (left to right), print stroke home. */
function carriage(p) {
  if (p < FLOOD[0]) return HOME
  if (p < FLOOD[1]) return lerp(HOME, FAR, stroke(seg(p, ...FLOOD)))
  if (p < PRINT[0]) return FAR
  if (p < PRINT[1]) return lerp(FAR, HOME, stroke(seg(p, ...PRINT)))
  return HOME
}

/** Screen frame: down with a cushion before the stroke, up level after it. */
function frame(p) {
  if (p < 0.21) return UP
  if (p < 0.27) return UP * (1 - pneu(seg(p, 0.21, 0.27), 0.01))
  if (p < 0.64) return 0
  if (p < 0.74) return UP * pneu(seg(p, 0.64, 0.74), 0.01)
  return UP
}

function squeegee(p) {
  if (p < 0.2) return SQ.up
  if (p < 0.23) return lerp(SQ.up, SQ.touch, pneu(seg(p, 0.2, 0.23)))
  if (p < 0.265) return SQ.touch
  if (p < 0.29) return lerp(SQ.touch, 0, easeOutCubic(seg(p, 0.265, 0.29)))
  if (p < 0.62) return 0
  if (p < 0.65) return lerp(0, SQ.up, pneu(seg(p, 0.62, 0.65)))
  return SQ.up
}

function floodBar(p) {
  if (p < 0.04) return FL.up
  if (p < 0.07) return FL.up * (1 - pneu(seg(p, 0.04, 0.07)))
  if (p < 0.2) return 0
  if (p < 0.23) return FL.up * pneu(seg(p, 0.2, 0.23))
  return FL.up
}

function flex(p) {
  if (p < 0.29 || p > 0.62) return 0
  if (p < 0.31) return 6 * easeOutCubic(seg(p, 0.29, 0.31))
  if (p < 0.6) return 6
  return 6 * (1 - easeInOutCubic(seg(p, 0.6, 0.62)))
}

/** The mesh V under the blade: pressed during the stroke, relaxes with a 2-unit overshoot. */
function meshV(p, sq) {
  if (p < 0.62) return Math.min(12, Math.max(0, 12 + sq))
  if (p >= 0.66) return 0
  const k = seg(p, 0.62, 0.66)
  return k < 0.6 ? lerp(12, -2, easeOutCubic(k / 0.6)) : lerp(-2, 0, smoothstep((k - 0.6) / 0.4))
}

/** Squeegee edge (world x): the top leads to the left, the edge trails by the lean plus the flex. */
const bladeEdge = (xs, deg) => xs + SQ.top + SQ.height * (SQ.lean + SQ.flexK * deg)

/* The pool rests just ahead of the squeegee edge at home; the flood bar
   catches it and drags it to the far stop, where the squeegee takes it. */
const POOL_HOME = bladeEdge(HOME, 0) - BEAD.print
const POOL_FAR = FAR + FL.edge + BEAD.flood
/* Where the flood coat ends: under the flood bar at the far stop. */
const FLOOD_END = FAR + FL.edge
/* The strip of coat the stroke never reaches, left of the edge at home: the
   film ends with it and starts with it. */
const LEFTOVER = bladeEdge(HOME, 0)

/**
 * Ink on the mesh: the right edge of the flood coat (the white openings fill
 * with it) and the bead, pushed by whichever blade is down. Behind the
 * squeegee the coat is wiped and the openings keep only a residue.
 */
function ink(p, xs, edge) {
  if (p < FLOOD[0]) return { coat: LEFTOVER, bead: POOL_HOME }
  if (p < PRINT[0]) {
    const bar = xs + FL.edge
    return { coat: Math.max(LEFTOVER, bar), bead: Math.max(POOL_HOME, bar + BEAD.flood) }
  }
  // The stroke starts with its edge past the coat the flood bar left: the coat
  // keeps that end until the blade comes back over it.
  return { coat: Math.max(COAT_FROM, Math.min(FLOOD_END, edge)), bead: Math.min(POOL_FAR, edge - BEAD.print) }
}

const MEDIUM = { s: 1.3, x: 830, y: 480 }
const track = (xs) => ({ s: 1.75, x: xs - 20, y: 505 })
const PANEL = { s: 1.4, x: 960, y: 525 }
const PANEL_CLOSE = { s: 1.45, x: 975, y: 520 }
const END = { s: 1.12, x: 830, y: 470 }
/* The match cut: the right scale printed on the panel (face 998, 562) and
   the same scale open in the raised stencil (mesh face 998, 550 - 150),
   framed alike. */
const CUT_S = 3
const STENCIL_CLOSE = { s: CUT_S, x: 998, y: 400 }
const PRINT_CLOSE = { s: CUT_S, x: 998, y: 562 }
const CAMERA = [
  [0, 0.07, STENCIL_CLOSE, MEDIUM],
  [0.255, PRINT[0], MEDIUM, track(FAR)],
  [PRINT[1], 0.7, track(HOME), PANEL],
  [0.74, 0.86, PANEL, PANEL_CLOSE],
  [0.86, 0.98, PANEL_CLOSE, PRINT_CLOSE],
]

/** @param {number} p film progress 0..1 */
export function screenFrame(p) {
  const xs = carriage(p)
  const cam = p >= PRINT[0] && p <= PRINT[1] ? track(xs) : cameraAt(p, CAMERA)
  const sq = squeegee(p)
  const deg = flex(p)
  const edge = bladeEdge(xs, deg)
  const { coat, bead } = ink(p, xs, edge)
  const moving = smoothstep(seg(p, PRINT[0], 0.33)) * (1 - smoothstep(seg(p, 0.59, PRINT[1])))
  return {
    vars: {
      ...cameraVars(cam),
      'pad-x': xs,
      'frame-dy': frame(p),
      'frame-tilt': 0,
      'sq-dz': sq,
      'fl-dz': floodBar(p),
      flex: deg,
      edge: edge - U0,
      'mesh-v': meshV(p, sq),
      'v-w': 40 + 20 * moving,
      bead: bead - U0,
      roll: ((bead % 12) + 12) % 12,
      coat: coat - U0,
      fill: coat - U0,
      // The panel print is uncovered as the screen lifts (swapped under the mesh).
      print: p >= 0.645 ? 1 : 0,
      ...register(p),
    },
    station: p < SCREEN_STATIONS[1] ? 0 : p < SCREEN_STATIONS[2] ? 1 : 2,
  }
}

/** The still: the scales printed and in register, the screen up, the machine in a medium shot. */
export function screenPoster() {
  const frame = screenFrame(0.84)
  return { ...frame, vars: { ...frame.vars, ...cameraVars(END) } }
}
